import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getMySessions } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import MobileAppLayout from "../../layouts/MobileAppLayout";
import { GameCard, GameHero, GameIcon, HudChip, SegmentedControl, Skeleton } from "../../components/game";

type SessionType = {
  id: string;
  title: string;
  start_at: string;
  end_at: string;
  type: string;
  location_text?: string;
  team?: { name: string };
};

export default function AppSessions() {
  const { t } = useTranslation();
  useAuth();

  const [sessions, setSessions] = useState<SessionType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState<"upcoming" | "past">("upcoming");

  useEffect(() => {
    const loadSessions = async () => {
      setLoading(true);
      setError(false);
      try {
        // Backend auto-scopes to the athlete's own team(s) — including every
        // team a multi-team athlete belongs to — so we no longer over-fetch
        // the whole club.
        const ss = await getMySessions();
        const mapped: SessionType[] = ss.map((s) => ({
          id: s.id,
          title: s.title,
          start_at: s.start_at,
          end_at: s.end_at,
          type: s.type,
          location_text: s.location?.name || s.location_text || undefined,
          team: s.team ? { name: s.team.name } : undefined,
        }));
        mapped.sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime());
        setSessions(mapped);
      } catch (err) {
        console.error("Failed to load sessions:", err);
        setError(true);
      }
      setLoading(false);
    };
    loadSessions();
  }, []);

  const now = new Date();
  const oneMonthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
  const filteredSessions = sessions.filter(s => {
    const sessionDate = new Date(s.start_at);
    if (filter === "upcoming") return sessionDate > now;
    // Past sessions: limit to last month for performance
    return sessionDate <= now && sessionDate >= oneMonthAgo;
  });

  const groupedSessions = filteredSessions.reduce((acc, session) => {
    const date = new Date(session.start_at).toLocaleDateString("ro-RO", {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
    if (!acc[date]) acc[date] = [];
    acc[date].push(session);
    return acc;
  }, {} as Record<string, SessionType[]>);

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" });
  };

  const getDaysUntil = (dateStr: string) => {
    const diff = new Date(dateStr).getTime() - now.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return t("sessions.today", "Azi");
    if (days === 1) return t("sessions.tomorrow", "Mâine");
    return `${days} ${t("sessions.daysLeft", "zile")}`;
  };

  return (
    <MobileAppLayout>
      <div className="pb-6">
        {/* Header */}
        <GameHero flow className="rounded-br-3xl px-4 pt-3 pb-4">
          <h1 className="text-xl font-extrabold">{t("sessions.title", "Antrenamente")}</h1>
          <p className="text-white/70 text-[13px] mt-0.5">{t("sessions.subtitle", "Programul tău de antrenamente")}</p>
        </GameHero>

        <div className="px-4 pt-5 space-y-4">
          {/* Filter Tabs */}
          <SegmentedControl<"upcoming" | "past">
            className="flex w-full [&>button]:flex-1"
            options={[
              { value: "upcoming", label: t("sessions.upcoming", "Viitoare") },
              { value: "past", label: t("sessions.past", "Trecute") },
            ]}
            value={filter}
            onChange={setFilter}
          />

          {/* Sessions List */}
          {loading ? (
            <div className="space-y-6 animate-fadeIn">
              {[0, 1].map((group) => (
                <div key={group}>
                  <Skeleton className="h-3 w-32 rounded-full mb-3 ml-1" />
                  <div className="space-y-3">
                    {Array.from({ length: group === 0 ? 3 : 2 }).map((_, i) => (
                      <Skeleton key={i} className="h-20 rounded-2xl" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <GameCard className="p-6 text-center">
              <p className="text-sm text-slate-500">
                {t("common.loadError", "Nu am putut încărca datele. Verifică conexiunea și încearcă din nou.")}
              </p>
              <button
                onClick={() => window.location.reload()}
                className="mt-3 px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white"
              >
                {t("common.retry", "Reîncearcă")}
              </button>
            </GameCard>
          ) : Object.keys(groupedSessions).length > 0 ? (
            <div className="space-y-6">
              {Object.entries(groupedSessions).map(([date, daySessions]) => (
                <div key={date}>
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3 px-1">
                    {date}
                  </h3>
                  <div className="space-y-3">
                    {daySessions.map((session) => (
                      <GameCard key={session.id} className="overflow-hidden">
                        <div className="flex">
                          {/* Time Column */}
                          <div className="w-20 bg-gradient-to-b from-indigo-500 to-blue-600 flex flex-col items-center justify-center py-4 text-white">
                            <span className="text-lg font-extrabold">{formatTime(session.start_at)}</span>
                            <span className="text-xs text-white/60">-</span>
                            <span className="text-sm text-white/80">{formatTime(session.end_at)}</span>
                          </div>

                          {/* Content */}
                          <div className="flex-1 p-4">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <h4 className="font-bold text-slate-800 truncate">{session.title}</h4>
                                {session.team && (
                                  <p className="text-sm text-slate-500 mt-0.5">{session.team.name}</p>
                                )}
                              </div>
                              <span className={`px-2.5 py-1 rounded-full text-xs font-bold whitespace-nowrap ${
                                session.type === "sport"
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-indigo-100 text-indigo-700"
                              }`}>
                                {session.type}
                              </span>
                            </div>

                            {/* Location */}
                            {session.location_text && (
                              <div className="flex items-center gap-2 mt-3 text-sm text-slate-500">
                                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                </svg>
                                <span className="truncate">{session.location_text}</span>
                              </div>
                            )}

                            {/* Days Until Badge */}
                            {filter === "upcoming" && (
                              <div className="mt-3">
                                <HudChip icon="clock" value={getDaysUntil(session.start_at)} />
                              </div>
                            )}
                          </div>
                        </div>
                      </GameCard>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <GameCard className="p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-2xl flex items-center justify-center">
                <GameIcon name="clock" className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-bold text-slate-800 mb-2">
                {filter === "upcoming"
                  ? t("sessions.noUpcoming", "Niciun antrenament programat")
                  : t("sessions.noPast", "Niciun antrenament trecut")
                }
              </h3>
              <p className="text-slate-500 text-sm">
                {filter === "upcoming"
                  ? t("sessions.noUpcomingDesc", "Antrenamentele viitoare vor apărea aici")
                  : t("sessions.noPastDesc", "Istoricul antrenamentelor tale va apărea aici")
                }
              </p>
            </GameCard>
          )}
        </div>
      </div>
    </MobileAppLayout>
  );
}
